import { useState, useMemo } from 'react';
import {
  AreaChart, Area,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import {
  TrendingUp, DollarSign, Users, QrCode,
  Building2, Tag, RotateCcw, Info,
  ArrowRight, Zap, BarChart2, Package,
} from 'lucide-react';

// ── Formatters ────────────────────────────────────────────────────────────────

const fmt  = (v, d = 0) => new Intl.NumberFormat('fr-MA', { minimumFractionDigits: d, maximumFractionDigits: d }).format(v);
const fmtDH = (v) => `${fmt(v, 0)} DH`;
const fmtK  = (v) => {
  if (v >= 1_000_000) return `${fmt(v / 1_000_000, 2)} M DH`;
  if (v >= 1_000)     return `${fmt(v / 1_000, 1)} k DH`;
  return fmtDH(v);
};

const MONTHS = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];

// ── Defaults ──────────────────────────────────────────────────────────────────

const DEFAULTS = {
  // Labs & products
  nbLabs:              20,
  promosParLab:         4,
  produitsParPromo:     3,
  etiquettesParProduit: 200,
  tauxUtilisation:      40,   // % of QR codes actually scanned
  // Vendors
  nbVendeurs:          300,
  scansParVendeur:      12,
  // Commissions (per scan)
  commissionMazid:      5,    // DH - Mazid revenue per scan
  commissionVendeur:   10,    // DH - Vendor revenue per scan (funded by lab, info only)
  // Operations & growth
  coutsMensuels:      5000,   // Mazid monthly operational costs
  croissance:           8,    // % monthly growth
};

// ── Slider ────────────────────────────────────────────────────────────────────

const Slider = ({ label, value, min, max, step = 1, unit = '', onChange, description, accent = 'green' }) => {
  const pct = Math.round(((value - min) / (max - min)) * 100);
  const colors = {
    green:  { bar: 'bg-green-500',  thumb: 'border-green-500',  badge: 'bg-green-50 text-green-700 border-green-200' },
    blue:   { bar: 'bg-blue-500',   thumb: 'border-blue-500',   badge: 'bg-blue-50 text-blue-700 border-blue-200'   },
    orange: { bar: 'bg-orange-400', thumb: 'border-orange-400', badge: 'bg-orange-50 text-orange-700 border-orange-200' },
    purple: { bar: 'bg-purple-500', thumb: 'border-purple-500', badge: 'bg-purple-50 text-purple-700 border-purple-200' },
  };
  const c = colors[accent] || colors.green;

  return (
    <div className="mb-5 last:mb-0">
      <div className="flex justify-between items-center mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-gray-700">{label}</span>
          {description && (
            <div className="relative group">
              <Info size={12} className="text-gray-400 cursor-help" />
              <div className="absolute left-0 bottom-full mb-2 w-56 bg-gray-900 text-white text-xs rounded-xl px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 shadow-xl leading-relaxed">
                {description}
              </div>
            </div>
          )}
        </div>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${c.badge}`}>
          {fmt(value)}{unit ? ' ' + unit : ''}
        </span>
      </div>
      <div className="relative h-2 bg-gray-100 rounded-full">
        <div className={`absolute h-full rounded-full ${c.bar}`} style={{ width: `${pct}%` }} />
        <input
          type="range" min={min} max={max} step={step} value={value}
          onChange={e => onChange(Number(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        <div
          className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-white border-2 rounded-full shadow-sm pointer-events-none ${c.thumb}`}
          style={{ left: `${pct}%` }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-gray-400 mt-1 font-medium">
        <span>{fmt(min)}{unit ? ' ' + unit : ''}</span>
        <span>{fmt(max)}{unit ? ' ' + unit : ''}</span>
      </div>
    </div>
  );
};

// ── KPI card ──────────────────────────────────────────────────────────────────

const Kpi = ({ label, value, sub, icon: Icon, bg, iconColor }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-3">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bg}`}>
      <Icon size={18} className={iconColor} />
    </div>
    <div>
      <div className="text-2xl font-black text-gray-900">{value}</div>
      <div className="text-xs font-semibold text-gray-500 mt-0.5">{label}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  </div>
);

// ── Section label ─────────────────────────────────────────────────────────────

const SectionLabel = ({ children, color = 'text-gray-600' }) => (
  <p className={`text-xs font-bold uppercase tracking-wider mb-3 ${color}`}>{children}</p>
);

// ── Flow arrow card ───────────────────────────────────────────────────────────

const FlowCard = ({ from, amount, to, color, sub }) => (
  <div className={`rounded-xl border px-4 py-3 flex items-center gap-3 ${color}`}>
    <div className="text-xs font-semibold text-gray-500 w-16 text-right shrink-0">{from}</div>
    <ArrowRight size={14} className="text-gray-400 shrink-0" />
    <div className="flex-1">
      <div className="font-black text-gray-900 text-sm">{amount}</div>
      {sub && <div className="text-xs text-gray-400">{sub}</div>}
    </div>
    <div className="text-xs font-semibold text-gray-500 w-16 shrink-0">{to}</div>
  </div>
);

// ── Custom tooltip ────────────────────────────────────────────────────────────

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 text-sm min-w-[180px]">
      <p className="font-bold text-gray-700 mb-2 border-b pb-1">{label}</p>
      {payload.map((e, i) => (
        <div key={i} className="flex items-center justify-between gap-4 mt-1">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: e.color }} />
            <span className="text-gray-600 text-xs">{e.name}</span>
          </div>
          <span className="font-bold text-gray-900 text-xs">{fmtDH(e.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ── Main ───────────────────────────────────────────────────────────────────────

export default function FinancialSimulator() {
  const [p, setP] = useState({ ...DEFAULTS });
  const set = key => v => setP(prev => ({ ...prev, [key]: v }));

  // ── Calculations ────────────────────────────────────────────────────────────
  //
  // MODEL:
  //  - Labs create promotions with products → generate QR labels
  //  - Vendors scan QR codes when selling promoted products
  //  - Each scan: lab "pays" (commissionVendeur + commissionMazid)
  //  - Mazid receives commissionMazid per scan → pure revenue
  //  - Mazid pays vendors commissionVendeur per scan (from lab funds)
  //  - Mazid NEVER loses: its revenue = commissionMazid × nb_scans
  //
  const calc = useMemo(() => {
    // ── Supply (QR codes available) ──
    const totalEtiquettes    = p.nbLabs * p.promosParLab * p.produitsParPromo * p.etiquettesParProduit;
    const etiquettesScannees = Math.round(totalEtiquettes * p.tauxUtilisation / 100);

    // ── Demand (scans from vendors) ──
    const scansVendeurs = p.nbVendeurs * p.scansParVendeur;

    // ── Effective scans = min(supply scanned, demand) ──
    const totalScans = Math.min(etiquettesScannees, scansVendeurs);
    const isLimitedBySupply = etiquettesScannees < scansVendeurs;

    // ── Revenue per scan ──
    const totalParScan   = p.commissionMazid + p.commissionVendeur;   // Lab pays this per scan

    // ── Mazid financials ──
    const revMazid       = totalScans * p.commissionMazid;            // Mazid's pure revenue
    const fluxVendeurs   = totalScans * p.commissionVendeur;          // Pass-through to vendors
    const fluxTotal      = totalScans * totalParScan;                 // Total managed by Mazid
    const beneficeNet    = revMazid - p.coutsMensuels;               // After operational costs
    const roi            = p.coutsMensuels > 0 ? (beneficeNet / p.coutsMensuels) * 100 : Infinity;

    // ── Annual projection with monthly compound growth ──
    const monthly = MONTHS.map((month, i) => {
      const mult = Math.pow(1 + p.croissance / 100, i);
      const scans = Math.round(totalScans * mult);
      const rM    = Math.round(scans * p.commissionMazid);
      const fV    = Math.round(scans * p.commissionVendeur);
      return {
        month,
        'Rev. Mazid':          rM,
        'Flux vendeurs':       fV,
        'Bénéfice net Mazid':  Math.max(0, rM - p.coutsMensuels),
      };
    });

    const annualRevMazid  = monthly.reduce((s, m) => s + m['Rev. Mazid'], 0);
    const annualBenef     = monthly.reduce((s, m) => s + m['Bénéfice net Mazid'], 0);
    const annualFlux      = monthly.reduce((s, m) => s + m['Flux vendeurs'], 0);

    // ── Sensitivity: revenue by nb of labs (others constant) ──
    const byLabs = [5, 10, 20, 50, 100, 200].map(n => {
      const etq = n * p.promosParLab * p.produitsParPromo * p.etiquettesParProduit;
      const scanned = Math.min(Math.round(etq * p.tauxUtilisation / 100), scansVendeurs);
      return { name: `${n} labos`, 'Rev. Mazid': Math.round(scanned * p.commissionMazid) };
    });

    // ── Sensitivity: revenue by nb of vendors ──
    const byVendeurs = [50, 100, 200, 500, 1000, 2000].map(n => {
      const scans = Math.min(n * p.scansParVendeur, etiquettesScannees);
      return { name: `${n} vend.`, 'Rev. Mazid': Math.round(scans * p.commissionMazid) };
    });

    // ── Pie: breakdown of total flux ──
    const pie = [
      { name: 'Mazid',    value: Math.round(revMazid),    color: '#16a34a' },
      { name: 'Vendeurs', value: Math.round(fluxVendeurs), color: '#0ea5e9' },
    ];

    return {
      totalEtiquettes, etiquettesScannees, scansVendeurs,
      totalScans, isLimitedBySupply, totalParScan,
      revMazid, fluxVendeurs, fluxTotal, beneficeNet, roi,
      monthly, annualRevMazid, annualBenef, annualFlux,
      byLabs, byVendeurs, pie,
    };
  }, [p]);

  const reset = () => setP({ ...DEFAULTS });

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-5 mb-6 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-black text-gray-900 flex items-center gap-2">
              <TrendingUp className="text-green-600" size={22} />
              Simulateur financier Mazid
            </h1>
            <p className="text-xs text-gray-400 mt-0.5">
              Modèle réel · Mazid = marketplace entre laboratoires et vendeurs en pharmacie
            </p>
          </div>
          <button onClick={reset} className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
            <RotateCcw size={13} /> Réinitialiser
          </button>
        </div>
      </div>

      {/* Model explanation banner */}
      <div className="mx-6 mb-6 bg-green-50 border border-green-200 rounded-2xl p-4">
        <p className="text-xs font-semibold text-green-800 mb-2 flex items-center gap-1.5">
          <Zap size={13} /> Modèle économique
        </p>
        <div className="flex flex-wrap items-center gap-2 text-xs text-green-700">
          <span className="bg-white border border-green-200 rounded-lg px-2.5 py-1 font-semibold">Labo</span>
          <ArrowRight size={12} />
          <span className="bg-white border border-green-200 rounded-lg px-2.5 py-1">crée promotion + produit</span>
          <ArrowRight size={12} />
          <span className="bg-white border border-green-200 rounded-lg px-2.5 py-1 font-semibold">Vendeur scan QR</span>
          <ArrowRight size={12} />
          <span className="bg-green-700 text-white rounded-lg px-2.5 py-1 font-semibold">Mazid +{fmtDH(p.commissionMazid)}/scan</span>
          <span className="text-gray-400">+</span>
          <span className="bg-blue-100 text-blue-700 rounded-lg px-2.5 py-1 font-semibold">Vendeur +{fmtDH(p.commissionVendeur)}/scan (payé par labo)</span>
        </div>
      </div>

      <div className="px-6 pb-10 grid grid-cols-1 xl:grid-cols-5 gap-6">

        {/* ── LEFT: Sliders ── */}
        <div className="xl:col-span-2 space-y-4">

          {/* Labs */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <SectionLabel color="text-blue-600">🏭 Laboratoires & Produits</SectionLabel>
            <Slider label="Laboratoires actifs" value={p.nbLabs} min={1} max={500} accent="blue"
              onChange={set('nbLabs')} description="Nombre de labos ayant des promotions actives sur Mazid" />
            <Slider label="Promotions / labo" value={p.promosParLab} min={1} max={30} accent="blue"
              onChange={set('promosParLab')} description="Nombre moyen de promotions simultanées par laboratoire" />
            <Slider label="Produits / promotion" value={p.produitsParPromo} min={1} max={20} accent="blue"
              onChange={set('produitsParPromo')} description="Nombre de produits différents dans chaque promotion" />
            <Slider label="Étiquettes QR / produit" value={p.etiquettesParProduit} min={10} max={5000} step={10} accent="blue"
              onChange={set('etiquettesParProduit')} description="Nombre de QR codes générés par produit (= stock de scans possibles)" />
            <Slider label="Taux d'utilisation des QR" value={p.tauxUtilisation} min={1} max={100} unit="%" accent="blue"
              onChange={set('tauxUtilisation')} description="% des QR codes qui seront effectivement scannés par les vendeurs" />
          </div>

          {/* Vendors */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <SectionLabel color="text-green-600">👨‍⚕️ Vendeurs en pharmacie</SectionLabel>
            <Slider label="Vendeurs actifs" value={p.nbVendeurs} min={10} max={10000} step={10} accent="green"
              onChange={set('nbVendeurs')} description="Nombre de pharmaciens/vendeurs actifs utilisant l'app Mazid" />
            <Slider label="Scans / vendeur / mois" value={p.scansParVendeur} min={1} max={200} accent="green"
              onChange={set('scansParVendeur')} description="Nombre moyen de QR codes scannés par vendeur chaque mois" />
          </div>

          {/* Commissions */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <SectionLabel color="text-purple-600">💰 Commissions par scan</SectionLabel>

            <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4">
              <p className="text-xs font-bold text-green-700 mb-0.5">Commission Mazid (votre revenu)</p>
              <p className="text-xs text-green-600">Montant que Mazid garde pour chaque QR scanné</p>
            </div>
            <Slider label="Commission Mazid / scan" value={p.commissionMazid} min={0.5} max={50} step={0.5} unit="DH" accent="green"
              onChange={set('commissionMazid')} description="Part Mazid par scan. Plus c'est élevé, plus Mazid gagne par transaction." />

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 mt-5">
              <p className="text-xs font-bold text-blue-700 mb-0.5">Commission vendeur (payée par le labo)</p>
              <p className="text-xs text-blue-600">Transit par Mazid → reversé au vendeur. N'impacte pas le bénéfice Mazid.</p>
            </div>
            <Slider label="Commission vendeur / scan" value={p.commissionVendeur} min={1} max={100} step={1} unit="DH" accent="blue"
              onChange={set('commissionVendeur')} description="Ce que le labo paye au vendeur. Mazid est juste intermédiaire pour ce flux." />

            <div className="mt-4 bg-gray-50 rounded-xl p-3 text-xs text-gray-600">
              <p className="font-semibold mb-1">Total que le labo paie par scan :</p>
              <p className="text-lg font-black text-gray-900">{fmtDH(p.commissionMazid + p.commissionVendeur)}</p>
              <p className="text-gray-400">= {fmtDH(p.commissionMazid)} Mazid + {fmtDH(p.commissionVendeur)} vendeur</p>
            </div>
          </div>

          {/* Operations */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <SectionLabel color="text-orange-600">⚙️ Coûts & Croissance</SectionLabel>
            <Slider label="Coûts opérationnels/mois" value={p.coutsMensuels} min={0} max={100000} step={500} unit="DH" accent="orange"
              onChange={set('coutsMensuels')} description="Coûts fixes de Mazid (infrastructure, salaires, etc.) pour calculer le bénéfice net" />
            <Slider label="Croissance mensuelle" value={p.croissance} min={0} max={50} step={0.5} unit="%" accent="purple"
              onChange={set('croissance')} description="Taux de croissance composé mensuel appliqué à la projection sur 12 mois" />
          </div>
        </div>

        {/* ── RIGHT: Results ── */}
        <div className="xl:col-span-3 space-y-5">

          {/* Scan alert */}
          {calc.isLimitedBySupply ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2 text-xs">
              <span className="text-amber-600 font-bold mt-0.5">⚠</span>
              <div>
                <span className="font-bold text-amber-700">Limitée par l'offre labo : </span>
                <span className="text-amber-600">
                  Les vendeurs pourraient faire {fmt(calc.scansVendeurs)} scans mais il n'y a que {fmt(calc.etiquettesScannees)} QR disponibles.
                  Ajoutez des labos, promotions ou produits pour débloquer le potentiel vendeur.
                </span>
              </div>
            </div>
          ) : (
            <div className="bg-sky-50 border border-sky-200 rounded-xl px-4 py-3 flex items-start gap-2 text-xs">
              <span className="text-sky-600 font-bold mt-0.5">ℹ</span>
              <div>
                <span className="font-bold text-sky-700">Limitée par la demande vendeur : </span>
                <span className="text-sky-600">
                  {fmt(calc.etiquettesScannees)} QR disponibles, vendeurs utilisent {fmt(calc.totalScans)}.
                  Vous pouvez recruter plus de vendeurs ou augmenter leur fréquence de scan.
                </span>
              </div>
            </div>
          )}

          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Kpi label="Scans / mois" value={fmt(calc.totalScans)} sub={`sur ${fmt(calc.etiquettesScannees)} QR dispo`}
              icon={QrCode} bg="bg-gray-100" iconColor="text-gray-600" />
            <Kpi label="Revenu Mazid / mois" value={fmtK(calc.revMazid)} sub={`${fmtDH(p.commissionMazid)}/scan`}
              icon={DollarSign} bg="bg-green-100" iconColor="text-green-700" />
            <Kpi label="Bénéfice net / mois" value={fmtK(calc.beneficeNet)} sub={`Après ${fmtK(p.coutsMensuels)} coûts`}
              icon={TrendingUp} bg={calc.beneficeNet >= 0 ? 'bg-emerald-100' : 'bg-red-100'} iconColor={calc.beneficeNet >= 0 ? 'text-emerald-700' : 'text-red-600'} />
            <Kpi label="Projection annuelle" value={fmtK(calc.annualRevMazid)} sub={`Bén. : ${fmtK(calc.annualBenef)}`}
              icon={BarChart2} bg="bg-purple-100" iconColor="text-purple-700" />
          </div>

          {/* Flow diagram */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className="text-sm font-bold text-gray-700 mb-4">Flux financiers mensuels ({fmt(calc.totalScans)} scans)</p>
            <div className="space-y-2">
              <FlowCard
                from="Labos" amount={fmtDH(calc.fluxTotal)}
                sub={`${fmtDH(calc.totalParScan)} / scan × ${fmt(calc.totalScans)} scans`}
                to="→ Mazid" color="border-gray-200 bg-gray-50"
              />
              <div className="pl-6 space-y-2">
                <FlowCard
                  from="Mazid" amount={fmtDH(calc.revMazid)}
                  sub={`${fmtDH(p.commissionMazid)}/scan · votre revenu`}
                  to="Mazid ✓" color="border-green-200 bg-green-50"
                />
                <FlowCard
                  from="Mazid" amount={fmtDH(calc.fluxVendeurs)}
                  sub={`${fmtDH(p.commissionVendeur)}/scan · transit`}
                  to="Vendeurs" color="border-blue-200 bg-blue-50"
                />
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
                <span className="text-xs text-gray-500 font-semibold">Volume total géré par Mazid</span>
                <span className="font-black text-gray-900">{fmtDH(calc.fluxTotal)}</span>
              </div>
            </div>
          </div>

          {/* Indicateurs réseau */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className="text-sm font-bold text-gray-700 mb-4">État du réseau</p>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'QR codes créés',    value: fmt(calc.totalEtiquettes),    icon: Tag,      color: 'text-blue-600',   bg: 'bg-blue-50' },
                { label: 'QR scannés/mois',   value: fmt(calc.etiquettesScannees), icon: QrCode,   color: 'text-green-600',  bg: 'bg-green-50' },
                { label: 'Capacité vendeurs', value: fmt(calc.scansVendeurs),      icon: Users,    color: 'text-purple-600', bg: 'bg-purple-50' },
                { label: 'Promotions actives',value: fmt(p.nbLabs * p.promosParLab), icon: Tag,    color: 'text-orange-600', bg: 'bg-orange-50' },
                { label: 'Produits en promo', value: fmt(p.nbLabs * p.promosParLab * p.produitsParPromo), icon: Package, color: 'text-pink-600', bg: 'bg-pink-50' },
                { label: 'Labs partenaires',  value: fmt(p.nbLabs),                icon: Building2,color: 'text-sky-600',    bg: 'bg-sky-50' },
              ].map((card, i) => (
                <div key={i} className={`${card.bg} rounded-xl p-3 flex items-center gap-2`}>
                  <card.icon size={14} className={card.color} />
                  <div>
                    <div className={`text-base font-black ${card.color}`}>{card.value}</div>
                    <div className="text-[10px] text-gray-500 font-medium leading-tight">{card.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bénéfice net summary */}
          <div className={`rounded-2xl border-2 p-5 ${calc.beneficeNet >= 0 ? 'border-green-200 bg-gradient-to-r from-green-50 to-emerald-50' : 'border-red-200 bg-red-50'}`}>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Bénéfice net mensuel Mazid</p>
                <p className={`text-4xl font-black mt-1 ${calc.beneficeNet >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                  {fmtK(calc.beneficeNet)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {fmtK(calc.revMazid)} revenus − {fmtK(p.coutsMensuels)} coûts opé.
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs font-semibold text-gray-500">Projection annuelle</p>
                <p className={`text-xl font-black ${calc.annualBenef >= 0 ? 'text-green-700' : 'text-red-600'}`}>{fmtK(calc.annualBenef)}</p>
                <p className="text-xs text-gray-400">{fmtK(calc.annualRevMazid)} revenus total</p>
              </div>
            </div>
          </div>

          {/* Monthly projection chart */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className="text-sm font-bold text-gray-800 mb-1">Projection 12 mois ({p.croissance}%/mois)</p>
            <p className="text-xs text-gray-400 mb-5">Revenu Mazid, flux vendeurs et bénéfice net projetés</p>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={calc.monthly} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
                <defs>
                  <linearGradient id="gMazid" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#16a34a" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gVend" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => fmtK(v)} width={65} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="Rev. Mazid"         stroke="#16a34a" fill="url(#gMazid)" strokeWidth={2.5} />
                <Area type="monotone" dataKey="Flux vendeurs"      stroke="#0ea5e9" fill="url(#gVend)"  strokeWidth={1.5} strokeDasharray="4 2" />
                <Area type="monotone" dataKey="Bénéfice net Mazid" stroke="#a855f7" fill="none"         strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Sensitivity charts */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <p className="text-sm font-bold text-gray-800 mb-1">Impact du nb de labos</p>
              <p className="text-xs text-gray-400 mb-4">Revenu Mazid/mois selon les labos</p>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={calc.byLabs} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => fmtK(v)} width={55} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="Rev. Mazid" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <p className="text-sm font-bold text-gray-800 mb-1">Impact du nb de vendeurs</p>
              <p className="text-xs text-gray-400 mb-4">Revenu Mazid/mois selon les vendeurs</p>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={calc.byVendeurs} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => fmtK(v)} width={55} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="Rev. Mazid" fill="#16a34a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Pie */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className="text-sm font-bold text-gray-800 mb-4">Répartition du volume financier mensuel total</p>
            <div className="flex items-center gap-6">
              <div style={{ width: 160, height: 160 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={calc.pie} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" paddingAngle={4}>
                      {calc.pie.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip formatter={v => fmtDH(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-3">
                {calc.pie.map((e, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ background: e.color }} />
                      <span className="text-sm text-gray-600">{e.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-black text-gray-900">{fmtDH(e.value)}</span>
                      <span className="text-xs text-gray-400 ml-1.5">
                        {calc.fluxTotal > 0 ? `${fmt(e.value / calc.fluxTotal * 100, 0)}%` : '—'}
                      </span>
                    </div>
                  </div>
                ))}
                <div className="border-t pt-2 flex justify-between items-center">
                  <span className="text-xs font-semibold text-gray-500">Total géré</span>
                  <span className="text-sm font-black text-gray-800">{fmtDH(calc.fluxTotal)}</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
